import type { ClipboardEvent } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  PATTERN_TYPES,
  nextAvailablePatternName,
  resizeMultipliers,
  type Pattern,
} from "../model/inp/patterns.ts";
import { flattenClipboardGrid, parseClipboardGrid } from "../model/clipboardGrid.ts";
import { handleGridArrowNav } from "./gridKeyboardNav.ts";
import { NumberInput } from "./fields.tsx";
import { useT } from "../i18n/hooks.ts";
import { optionLabelKey } from "../i18n/fieldLabels.ts";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_SERIES_PRIMARY,
  CHART_TOOLTIP_STYLE,
} from "./chartTheme.ts";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function multiplierLabel(type: string, index: number): string {
  if (type === "MONTHLY") return MONTH_LABELS[index] ?? String(index);
  if (type === "DAILY") return WEEKDAY_LABELS[index] ?? String(index);
  return String(index);
}

export function PatternsEditor({
  patterns,
  onChange,
  selectedName,
  onSelect,
}: {
  patterns: Pattern[];
  onChange: (patterns: Pattern[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deletePattern,
    rename: renameSelected,
    update: updateSelected,
  } = useNamedCollection(patterns, onChange, selectedName, onSelect);

  function addPattern() {
    const name = nextAvailablePatternName(new Set(patterns.map((p) => p.name)));
    const type = PATTERN_TYPES[2]; // HOURLY
    add({ name, type, multipliers: resizeMultipliers([], type) });
  }

  function setMultiplier(index: number, value: number) {
    updateSelected((p) => ({
      ...p,
      multipliers: p.multipliers.map((v, j) => (j === index ? value : v)),
    }));
  }

  function handleMultipliersPaste(e: ClipboardEvent<HTMLInputElement>, startIndex: number) {
    e.preventDefault();
    const values = flattenClipboardGrid(parseClipboardGrid(e.clipboardData.getData("text")));
    updateSelected((p) => {
      const multipliers = [...p.multipliers];
      values.forEach((cell, offset) => {
        const idx = startIndex + offset;
        if (idx >= multipliers.length) return;
        const value = Number(cell);
        if (!Number.isNaN(value)) multipliers[idx] = value;
      });
      return { ...p, multipliers };
    });
  }

  const chartData = selected
    ? selected.multipliers.map((value, i) => ({
        label: multiplierLabel(selected.type, i),
        value,
      }))
    : [];

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={patterns}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addPattern}
        addLabel={t("projectData.addPattern")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("projectData.noPatternSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deletePattern(selected.name)}
              deleteLabel={t("projectData.deletePattern")}
            >
              <label className="field-pill">
                <span className="field-pill-label">{t("projectData.patternType")}</span>
                <select
                  value={selected.type}
                  onChange={(e) =>
                    updateSelected((p) => ({
                      ...p,
                      type: e.target.value,
                      multipliers: resizeMultipliers(p.multipliers, e.target.value),
                    }))
                  }
                >
                  {PATTERN_TYPES.map((type) => {
                    const key = optionLabelKey(type);
                    return (
                      <option key={type} value={type}>
                        {key ? t(key) : type}
                      </option>
                    );
                  })}
                </select>
              </label>
            </NamedCollectionHeader>
            <div className="curve-editor-body">
              <div className="curve-editor-table">
                <span className="curve-editor-section-title">{t("projectData.valuesTable")}</span>
                <div className="named-collection-points-table-wrap">
                  <table className="named-collection-points-table">
                    <thead>
                      <tr>
                        <th>{t("projectData.patternPeriod")}</th>
                        <th>{t("projectData.value")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.multipliers.map((value, i) => (
                        <tr key={i}>
                          <td className="pattern-table-label-cell">
                            {multiplierLabel(selected.type, i)}
                          </td>
                          <td>
                            <NumberInput
                              value={value}
                              onChange={(v) => setMultiplier(i, v)}
                              onPaste={(e) => handleMultipliersPaste(e, i)}
                              onKeyDown={handleGridArrowNav}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="curve-editor-chart">
                <span className="curve-editor-section-title">{t("projectData.chartPreview")}</span>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} margin={{ top: 4, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis dataKey="label" tick={CHART_AXIS_TICK} />
                    <YAxis tick={CHART_AXIS_TICK} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Bar dataKey="value" fill={CHART_SERIES_PRIMARY} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
